import { useState } from "react";
import { useToken } from "@/pages/token-v2/hooks/use-token";
import { formatToDecimals } from "@/utils/currency";

export interface BuyBotActionsState {
  amountToken: string;
  amountChain: string;
  slippage: string;
  isInstant: boolean;
  triggerPrice: string;
  buyGas: string;
  youPayNative: string;
  youPayUSD: string;
  youReceive: string;
  limit: string;
  balancePercent: string;
}

export const useBuyBotActions = () => {
  const token = useToken();
  const [state, setState] = useState<BuyBotActionsState>({
    amountToken: "",
    amountChain: "",
    slippage: "",
    isInstant: true,
    triggerPrice: "",
    buyGas: "",
    youPayNative: "",
    youPayUSD: "",
    youReceive: "",
    limit: "",
    balancePercent: "",
  });

  const pairInfo = token?.botPair || { nativePrice: 0, priceInNative: 0 };

  const handleYouPayNativeChange = (value: string, balancePercent?: string) => {
    const parsedValue = Number(value);
    const numValue = isNaN(parsedValue) ? 0 : parsedValue;

    let youPayUsd = 0;
    let youReceive = 0;

    if (state.isInstant) {
      youPayUsd = numValue * pairInfo.nativePrice;

      if (pairInfo.priceInNative && pairInfo.priceInNative > 0) {
        youReceive = numValue / pairInfo.priceInNative;
      }
    } else if (state.triggerPrice) {
      const triggerPrice = Number(state.triggerPrice);
      youPayUsd = numValue * pairInfo.nativePrice;

      if (triggerPrice && !isNaN(triggerPrice)) {
        youReceive = (numValue * pairInfo.nativePrice) / triggerPrice;
      }
    }

    let balancePercentValue = "";

    if (typeof balancePercent === "string") {
      balancePercentValue = balancePercent;
    } else if (
      token?.balanceNative &&
      !isNaN(Number(token.balanceNative)) &&
      Number(token.balanceNative) > 0
    ) {
      const balancePercentValueNum =
        (numValue / Number(token.balanceNative)) * 100;
      balancePercentValue =
        balancePercentValueNum > 0
          ? formatToDecimals(balancePercentValueNum)
          : "0";
    }

    setState((prev) => ({
      ...prev,
      youPayNative: value,
      youPayUSD: youPayUsd > 0 ? formatToDecimals(youPayUsd, 4) : "0",
      youReceive: youReceive === 0 ? "0" : formatToDecimals(youReceive, 8),
      balancePercent: balancePercentValue,
    }));
  };

  const handleYouPayUSDChange = (value: string) => {
    const parsedValue = Number(value);
    const numValue = isNaN(parsedValue) ? 0 : parsedValue;

    let youPayNative = 0;
    let youReceive = 0;

    if (state.isInstant) {
      if (pairInfo.nativePrice) {
        youPayNative = numValue / pairInfo.nativePrice;
      }
      if (pairInfo.priceInNative) {
        youReceive = youPayNative / pairInfo.priceInNative;
      }
    } else if (state.triggerPrice) {
      const triggerPrice = Number(state.triggerPrice);

      if (!isNaN(triggerPrice) && triggerPrice > 0) {
        if (pairInfo.nativePrice > 0) {
          youPayNative = (numValue / pairInfo.nativePrice) * triggerPrice;
        }
        youReceive = numValue / triggerPrice;
      }
    }

    let balancePercent = 0;

    if (token?.balanceNative && Number(token.balanceNative) > 0) {
      balancePercent = (youPayNative / Number(token.balanceNative)) * 100;
    }

    setState((prev) => ({
      ...prev,
      youPayUSD: value,
      youPayNative: formatToDecimals(youPayNative, 8),
      youReceive: youReceive === 0 ? "0" : formatToDecimals(youReceive, 8),
      balancePercent:
        balancePercent > 0 ? formatToDecimals(balancePercent) : "0",
    }));
  };

  const handleYouReceiveChange = (value: string) => {
    const parsedValue = Number(value);
    const numValue = isNaN(parsedValue) ? 0 : parsedValue;

    let youPayNative = 0;
    let youPayUsd = 0;

    if (state.isInstant) {
      youPayNative = numValue * pairInfo.priceInNative;
      youPayUsd = youPayNative * pairInfo.nativePrice;
    } else if (state.triggerPrice) {
      const triggerPrice = Number(state.triggerPrice);

      if (!isNaN(triggerPrice) && triggerPrice > 0) {
        youPayUsd = numValue * triggerPrice;
      }
      if (pairInfo.nativePrice > 0) {
        youPayNative = youPayUsd / pairInfo.nativePrice;
      }
    }

    let balancePercent = 0;
    if (token?.balanceNative && Number(token.balanceNative) > 0) {
      balancePercent = (youPayNative / Number(token.balanceNative)) * 100;
    }

    setState((prev) => ({
      ...prev,
      youReceive: value,
      youPayNative: formatToDecimals(youPayNative, 8),
      youPayUSD: youPayUsd > 0 ? formatToDecimals(youPayUsd, 4) : "0",
      balancePercent:
        balancePercent > 0 ? formatToDecimals(balancePercent) : "0",
    }));
  };

  const handleTriggerPriceChange = (value: string) => {
    const parsedTrigger = Number(value);
    const validTrigger = !isNaN(parsedTrigger) && parsedTrigger > 0;

    const youPayNative = Number(state.youPayNative);
    const validNative = !isNaN(youPayNative) && youPayNative > 0;

    let youReceive = 0;

    if (validTrigger && validNative) {
      youReceive = (youPayNative * pairInfo.nativePrice) / parsedTrigger;
    }

    setState((prev) => ({
      ...prev,
      triggerPrice: value,
      youReceive: youReceive === 0 ? "0" : formatToDecimals(youReceive, 8),
    }));
  };

  const handleInstantChange = (value: boolean) => {
    if (value) {
      setState((prev) => ({ ...prev, limit: "", isInstant: true }));
    } else {
      setState((prev) => ({ ...prev, isInstant: false }));
    }
  };

  const handleLimitChange = (limit: string) => {
    if (limit === "") {
      setState((prev) => ({
        ...prev,
        limit: "",
      }));
      handleTriggerPriceChange("");
    } else {
      const limitValue = isNaN(Number(limit)) ? 0 : Number(limit);
      const price = (token?.price ?? 0) * (1 + limitValue / 100);

      setState((prev) => ({
        ...prev,
        limit,
      }));
      handleTriggerPriceChange(String(price));
    }
  };

  const handleBalancePercentChange = (balancePercent: string) => {
    const amount =
      (Number(token?.balanceNative ?? 0) * Number(balancePercent)) / 100;

    handleYouPayNativeChange(String(amount), balancePercent);
  };

  return {
    state,
    setState,
    handlers: {
      handleYouPayNativeChange,
      handleYouPayUSDChange,
      handleYouReceiveChange,
      handleInstantChange,
      handleLimitChange,
      handleBalancePercentChange,
    },
  };
};
